import {
  type InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { runsApi } from '@/api/workspaces'
import { ROUTES } from '@/constants/routes'
import { runsCopy } from '@/copy'
import type { RunInfo, RunStatus, RunsPage } from '@/types/api'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Chip } from '@/ui/Chip/Chip'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Select } from '@/ui/Field/Field'
import { Link } from '@/ui/Link/Link'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { Pagination } from '@/ui/Pagination/Pagination'
import { lifecycleTone } from '@/ui/Status/lifecycle'
import { StatusDot } from '@/ui/Status/Status'
import { Table, type TableColumn } from '@/ui/Table/Table'
import { formatNumber, formatUsd } from '../utils/format'
import { formatDateTime, formatDuration } from '../utils/time'
import { RefreshFailed } from './RefreshFailed'
import styles from './RunsExplorer.module.css'

/**
 * Run history table for one workspace. Each row's status cell links to
 * `RunDetailPage`, which renders the trace; clicking the rest of the row does
 * the same.
 *
 * Extracted from the admin Runs tab so workspace members can inspect their own
 * agents' runs — the API was always workspace-scoped and member-authorized,
 * only the UI was admin-gated. The admin tab passes a cross-workspace picker
 * as `toolbar`; the agent profile passes `agentId` to narrow the list. The
 * page shell supplies any heading, so `caption` only names the table.
 *
 * Props: `workspaceId` scopes the query; `agentId` filters to one agent;
 * `queryScope` keeps admin and workspace caches apart; `caption` names the
 * table; `toolbar` renders after the status filter; `returnTo` is where the
 * run detail page's back/crumb returns (defaults to the workspace Runs page).
 */

const STATUS_FILTERS: Array<RunStatus | ''> = [
  '',
  'ok',
  'truncated',
  'error',
  'timeout',
  'cancelled',
  'running',
  'crashed',
]

const PAGE_SIZE = 100

interface Props {
  workspaceId: string
  agentId?: string
  queryScope?: string
  caption: string
  toolbar?: ReactNode
  returnTo?: string
}

export function RunsExplorer({
  workspaceId,
  agentId,
  queryScope = 'workspace',
  caption,
  toolbar,
  returnTo,
}: Props) {
  const navigate = useNavigate()
  const [status, setStatus] = useState<RunStatus | ''>('')

  const queryClient = useQueryClient()
  const queryKey = ['runs', queryScope, workspaceId, status]
  // Pages append and never replace. A refetch re-reads every loaded page in
  // order, so the list stays contiguous when new runs land on top.
  const {
    data,
    isLoading,
    isError,
    isFetchNextPageError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }): Promise<RunsPage> =>
      runsApi.listWorkspaceRuns(workspaceId, {
        status: status || undefined,
        limit: PAGE_SIZE,
        before: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) =>
      last.has_more ? (last.next_cursor ?? undefined) : undefined,
    enabled: !!workspaceId,
  })

  // Changing the filter or workspace starts over: the key it leaves keeps only
  // its first page, so coming back to it does not restore appended pages.
  const keyRef = useRef(queryKey)
  const keyId = JSON.stringify(queryKey)
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once per key change
  useEffect(() => {
    const prev = keyRef.current
    keyRef.current = queryKey
    if (JSON.stringify(prev) === keyId) return
    queryClient.setQueryData<InfiniteData<RunsPage, string | undefined>>(
      prev,
      (d) =>
        d && {
          pages: d.pages.slice(0, 1),
          pageParams: d.pageParams.slice(0, 1),
        },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyId])
  const showSkeleton = useSkeletonDelay(isLoading)
  const runs: RunInfo[] = data?.pages.flatMap((p) => p.runs) ?? []
  const retry = () => void refetch()

  const filtered = agentId ? runs.filter((r) => r.agent_id === agentId) : runs

  const columns: TableColumn<RunInfo>[] = [
    {
      key: 'status',
      header: runsCopy.columns.status,
      render: (r) => (
        <Link
          variant="quiet"
          href={ROUTES.runDetail(workspaceId, r.id)}
          state={{ returnTo }}
        >
          <StatusDot tone={lifecycleTone(r.status)} label={r.status} />
          <span className={styles.visuallyHidden}>
            {runsCopy.rowLink(r.id.slice(0, 8))}
          </span>
        </Link>
      ),
    },
    {
      key: 'agent',
      header: runsCopy.columns.agent,
      render: (r) => (
        <Inline gap="space-8" align="baseline" wrap={false}>
          <Text variant="code" tone="text-sub">
            {r.agent_id}
          </Text>
          <Chip variant="kind" label={r.model} />
          {r.error_message && (
            <Text variant="caption" tone="text-mute">
              {r.error_message.slice(0, 80)}
            </Text>
          )}
        </Inline>
      ),
    },
    {
      key: 'llm',
      header: runsCopy.columns.llm,
      align: 'end',
      render: (r) => <Text tone="text-sub">{r.llm_calls}</Text>,
    },
    {
      key: 'tools',
      header: runsCopy.columns.tools,
      align: 'end',
      render: (r) => <Text tone="text-sub">{r.tool_calls}</Text>,
    },
    {
      key: 'tokens',
      header: runsCopy.columns.tokens,
      align: 'end',
      render: (r) => (
        <Text tone="text-sub">
          {formatNumber(r.input_tokens + r.output_tokens)}
        </Text>
      ),
    },
    {
      key: 'cost',
      header: runsCopy.columns.cost,
      align: 'end',
      render: (r) => (
        <Text tone="text-sub">{formatUsd(r.est_cost_usd, 4)}</Text>
      ),
    },
    {
      key: 'duration',
      header: runsCopy.columns.duration,
      align: 'end',
      render: (r) => (
        <Text tone="text-sub">{formatDuration(r.duration_ms)}</Text>
      ),
    },
    {
      key: 'started',
      header: runsCopy.columns.started,
      align: 'end',
      render: (r) => (
        <Text tone="text-sub">{formatDateTime(r.started_at)}</Text>
      ),
    },
  ]

  return (
    <Stack gap="space-12" className={styles.root}>
      {/* D223: the filter's label is visually hidden (D8) and its value is the
          hint; the row aligns on the boxes' tops. */}
      <Inline gap="space-8" align="start">
        <div className={styles.filter}>
          <Select
            aria-label={runsCopy.filterLabel}
            value={status}
            onChange={(e) => setStatus(e.target.value as RunStatus | '')}
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s || 'all'} value={s}>
                {s || runsCopy.allStatuses}
              </option>
            ))}
          </Select>
        </div>
        {toolbar}
      </Inline>
      {isError && data && (
        <RefreshFailed
          message={
            isFetchNextPageError
              ? runsCopy.loadMoreFailed
              : runsCopy.refreshFailed
          }
          onRetry={retry}
        />
      )}
      {/* Busy from the first fetch; the skeleton rows wait for the delay. */}
      <div aria-busy={isLoading || undefined}>
        <Table
          caption={caption}
          columns={columns}
          rows={filtered}
          getRowId={(r) => r.id}
          onRowClick={(r) =>
            void navigate(ROUTES.runDetail(workspaceId, r.id), {
              state: { returnTo },
            })
          }
          loading={showSkeleton}
          error={
            isError && !data ? (
              <Alert tone="danger">
                <Inline gap="space-8" align="baseline" wrap>
                  <Text>{runsCopy.loadFailed}</Text>
                  <Button variant="outline" size="sm" onClick={retry}>
                    {runsCopy.retry}
                  </Button>
                </Inline>
              </Alert>
            ) : undefined
          }
          empty={
            isLoading ? null : status ? (
              <EmptyState
                variant="inline"
                description={runsCopy.filteredEmpty(status)}
                action={
                  <Button variant="outline" onClick={() => setStatus('')}>
                    {runsCopy.clearFilter}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                variant="inline"
                description={
                  workspaceId ? runsCopy.emptyNone : runsCopy.emptyNoWorkspace
                }
              />
            )
          }
        />
      </div>
      {hasNextPage && (
        <Pagination
          hasMore
          onLoadMore={() => void fetchNextPage()}
          loading={isFetchingNextPage}
          loaded={filtered.length}
        />
      )}
    </Stack>
  )
}
